import { useEffect, useRef, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import { projectImageCrop } from "lib/utils/projectImageCrop";
import "css/projectWorkspace.css";

const DEFAULTS = { aspect: "original", zoom: 1, x: 50, y: 50, size: 1024 };

export default function ProjectTreeImageEditor({ file, nodeName, onSave, onClose }) {
  const dialogRef = useRef(null);
  const canvasRef = useRef(null);
  const savingRef = useRef(false);
  const [image, setImage] = useState(null);
  const [options, setOptions] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previous?.focus?.(); };
  }, []);
  useEffect(() => {
    let active = true;
    const url = URL.createObjectURL(file);
    const source = new Image();
    source.onload = () => { if (active) setImage(source); };
    source.onerror = () => { if (active) setError("이미지를 읽을 수 없습니다. 다른 사진을 선택해 주세요."); };
    source.src = url;
    return () => { active = false; source.onload = null; source.onerror = null; URL.revokeObjectURL(url); };
  }, [file]);
  const crop = image ? projectImageCrop(image.naturalWidth, image.naturalHeight, options) : null;
  useEffect(() => {
    if (!image) return;
    const canvas = canvasRef.current;
    const rect = projectImageCrop(image.naturalWidth, image.naturalHeight, options);
    canvas.width = rect.width; canvas.height = rect.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.width, rect.height);
  }, [image, options]);
  const save = async () => {
    if (!image || savingRef.current) return;
    savingRef.current = true; setBusy(true); setError("");
    try {
      const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve, "image/webp", 0.9));
      if (!blob) throw new Error("이미지 변환에 실패했습니다.");
      const extension = blob.type === "image/webp" ? "webp" : "png";
      const output = new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.${extension}`, { type: blob.type });
      const result = await onSave(output);
      if (result?.success) onClose();
      else setError(result?.message || "이미지 저장에 실패했습니다. 다시 시도해 주세요.");
    } catch (e) { setError(e.message || "이미지 저장에 실패했습니다."); }
    finally { savingRef.current = false; setBusy(false); }
  };
  return <dialog ref={dialogRef} className="projectImageEditor" aria-labelledby="projectImageEditorTitle"
    onCancel={event => { event.preventDefault(); if (!savingRef.current) onClose(); }}>
    <div className="projectImageEditor__header"><div><h2 id="projectImageEditorTitle">대표 이미지 편집</h2><p>{nodeName} · 저장 버튼을 눌러야 변경 사항이 적용됩니다.</p></div>
      <button type="button" aria-label="편집 닫기" disabled={busy} onClick={onClose}><X size={20} aria-hidden="true" /></button></div>
    <div className="projectImageEditor__preview" aria-busy={!image && !error}>
      <canvas ref={canvasRef} role="img" aria-label={`${nodeName} 대표 이미지 편집 미리보기`} />
      {!image && !error && <p role="status">사진을 불러오는 중…</p>}
    </div>
    <fieldset disabled={busy || !image} className="projectImageEditor__controls"><legend>사진 조절</legend>
      <label>사진 비율<select value={options.aspect} onChange={e => setOptions({ ...options, aspect: e.target.value })}>
        <option value="original">원본 비율</option><option value="1">정사각형</option><option value={4 / 3}>4:3</option><option value={16 / 9}>16:9</option></select></label>
      <label>저장 크기 (긴 변)<select value={options.size} onChange={e => setOptions({ ...options, size: Number(e.target.value) })}>
        {[640, 1024, 2048].map(size => <option key={size} value={size}>{size}px 이하</option>)}</select></label>
      {[['zoom', '확대 / 축소', 1, 3, 0.05], ['x', '좌우 위치', 0, 100, 1], ['y', '상하 위치', 0, 100, 1]].map(([key, label, min, max, step]) =>
        <label key={key}>{label}<span>{key === "zoom" ? `${Math.round(options[key] * 100)}%` : `${options[key]}%`}</span>
          <input type="range" min={min} max={max} step={step} value={options[key]} onChange={e => setOptions({ ...options, [key]: Number(e.target.value) })} /></label>)}
    </fieldset>
    <p className="projectImageEditor__note">{crop ? `${crop.width} × ${crop.height}px · ` : ""}WebP로 저장합니다. GIF는 정지 이미지로 저장됩니다.</p>
    {error && <p role="alert" className="projectImageEditor__error">{error}</p>}
    <div className="projectImageEditor__footer"><button type="button" disabled={busy} onClick={() => setOptions(DEFAULTS)}><RotateCcw size={15} aria-hidden="true" /> 초기화</button>
      <button type="button" disabled={busy} onClick={onClose}>취소</button><button type="button" className="is-primary" disabled={busy || !image} onClick={save}>{busy ? "저장 중…" : "이미지 저장"}</button></div>
  </dialog>;
}
